import styled, { css, keyframes } from 'styled-components';
import Eyebrow from '@klv/atoms/eyebrow';
import Image from '@klv/atoms/image';
import Buttons from '@klv/molecules/buttons';
import Headline from '@klv/atoms/headline';
import Icon from '@klv/atoms/icon';
import { manualPadding } from '@klv/shared/styles/padding';
import { hasImage } from '@klv/shared/utils/functions';
import Grid from '@klv/utils/grid';
import useFirstRender from '@klv/hooks/useFirstRender';
import useSlides from '@klv/hooks/useSlides';

import type { TStyledAs } from '@klv/atoms/headline/types';
import type { IButtonProps } from '@klv/atoms/button/types';
import type { IButtons } from '@klv/molecules/buttons/types';
import type { TColorVariant } from '@klv/shared/styles/types';
import type { IGatsbyImageData } from 'gatsby-plugin-image';

type TCopyContentBackgroundLegacyColors = 'lavender' | 'sage';
type TCopyContentBackgrounds =
  'violet' | 'poppy' | 'eggplant'

type TDirectionOptions = 'left' | 'right';

interface ISocialProofSlide {
  buttons?: IButtonProps[];
  copyContentBackground: TCopyContentBackgrounds | TCopyContentBackgroundLegacyColors;
  current: boolean;
  dir: TDirectionOptions;
  eyebrowText?: string;
  headlineStyle: 'quoteSmall' | 'quoteLarge' | 'quoteSmallWhite' | 'quoteLargeWhite';
  headlineText?: string;
  image: {
    altText?: string;
    gatsbyImage: IGatsbyImageData;
  };
  previous: boolean;
}

interface ITransientProps {
  $current: boolean;
  $dir: TDirectionOptions;
  $firstRender: boolean;
  $previous: boolean;
}

export interface ISocialProof {
  mainBackground?: TColorVariant;
  slides: ISocialProofSlide[];
}

// create our slide in animations for images
const imageAnimations = {
  left: keyframes`
    0% {
      transform: translateX(100%);
    }
    100% {
      transform: translateX(0);
    }
  `,
  right: keyframes`
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(0);
    }
  `,
};
const StyledSocialProof = styled.div<{ $mainBackground: TColorVariant; }>`
  ${manualPadding}
  ${({ theme, $mainBackground }) => `
    background-color: ${$mainBackground === 'dark' ? theme.socialProof.mainBackground.dark : theme.socialProof.mainBackground.light};
    width: 100%;
    ${theme.globalPadding};
  `}
`;
const SlideContainer = styled(Grid)`
  grid-row: 1;
  grid-column: 1;
  margin: 0;
  position: relative;
  padding: 0;
`;
const ImageWrapper = styled.div`
  ${({ theme }) => css`
    overflow: hidden;
    align-self:center;
    grid-column: 7 / -1;
    grid-row: 1;
    display: none;
    ${theme.screenSize.s1200.mediaQuery} {
      display: grid;
      max-height: 53.7rem;
    }
    ${theme.screenSize.s1366.mediaQuery} {
        height: 53.7rem;
    }
  `}
`;
const ImageSlider = styled.div<ITransientProps>`
  ${({
    $current, $previous, $dir, $firstRender,
  }) => css`
    grid-row: 1;
    grid-column: 1;
    display: block;
    z-index: 0;
    opacity: 0;
    animation-timing-function: ease-in-out;
    transition-duration: .5s;
    animation-duration: .5s;
    transform: translateX(0);
    pointer-events: none;
    ${$firstRender && css`
      transition-duration: 0;
      animation-duration: 0;
      &:first-of-type {
        z-index: 2;
        opacity: 1;
      }
    `}
    ${$current && css`
      z-index: 2;
      opacity: 1;
      animation-name: ${imageAnimations[$dir]};
    `}
    ${$previous && css`
      z-index: 1;
      opacity: 1;
    `}
  `}
`;
const StyledImage = styled(Image)`
  ${({ theme }) => `
    ${theme.screenSize.s1200.mediaQuery} {
      max-height: 53.7rem;
    }
    ${theme.screenSize.s1366.mediaQuery} {
      height: 53.7rem;
    }
  `}
`;
const CopyContent = styled.div<{
  $copyContentBackground: TCopyContentBackgrounds | TCopyContentBackgroundLegacyColors;
  $current: boolean;
}>`
  ${({
    $copyContentBackground, theme, $current,
  }) => css`
    grid-column: 1 / -1;
    grid-row: 1;
    width: 100%;
    min-height: 51.7rem;
    padding: 2rem 2rem 2.8rem;
    ${theme.screenSize.s768.mediaQuery} {
      min-height: 64.1rem;
      padding: 4.4rem 6rem 4.2rem 6rem;
    }
    ${theme.screenSize.s1024.mediaQuery} {
      min-height: 57.6rem;
      padding: 4.2rem 7.8rem 6.4rem;
    }
    ${theme.screenSize.s1200.mediaQuery} {
      grid-column: 1 / 7;
      padding: 4.5rem 7rem 5.2rem 5.2rem;
      width: calc(100% + ${theme.screenSize.s1200.gridColumnGap}rem);
      min-height: 67.6rem;
      height: 67.6rem;
    }
    background-color: ${theme.socialProof.copyContentBackground[$copyContentBackground]};
    opacity: ${!$current ? '0' : '1'};
    visibility: ${!$current ? 'hidden' : 'visible'};
    transition-property: opacity, visibility;
    transition-timing-function: ease-in-out;
    transition-duration: .4s;
  `}
`;
const Spacer = styled.div`
  ${({ theme }) => `
    margin:  1.2rem 0rem 3.5rem;
    ${theme.screenSize.s768.mediaQuery} {
      margin:  2rem 0rem 4.2rem;
    }
    ${theme.screenSize.s1024.mediaQuery} {
      margin:  3rem 0rem 8.8rem;
    }
    ${theme.screenSize.s1200.mediaQuery} {
      margin:  2.8rem 0rem 5.4rem;
    }
  `}
`;
const SlideButtonContainer = styled.div`
  ${({ theme }) => `
    grid-column: 1 / -1;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    z-index: 1;
    bottom: 0rem;
    position: absolute; 
    width:100%;
    padding: 0rem 2rem 1rem;
    svg {
      fill: ${theme.carousel.arrows};
      width: 4.597rem;
    }
    ${theme.screenSize.s768.mediaQuery} {
      padding: 0rem 5rem 2.5rem;
      svg {
        width: 6.597rem;
      }
    }
    ${theme.screenSize.s1024.mediaQuery} {
      padding: 0rem 7rem 5rem;
    }
    ${theme.screenSize.s1200.mediaQuery} {
      padding: 0rem 5rem 4.5rem;
      grid-column: 1 / 7;
    }
  `}
`;
const SlideButton = styled.button`
  padding: 0;
  background-color: transparent;
  border: 0 solid transparent;
  text-decoration: none;
  display: inline-block;
  cursor: pointer;
  text-decoration: none;
  &:active {
    top: 1px;
  }
  &:first-of-type {
    margin-right: 2rem;
  }
`;
const ButtonContainer = styled.div``;
const ProgressContainer = styled.div`
  ${({ theme }) => `
    ${theme.fonts.sohne.regular};
    font-size: 1.6rem;
    letter-spacing: 0.2em;
  `}
  color: ${({ theme }) => theme.colors.white};
`;
const SocialProofWrapper = styled.div`
  margin: auto;
  display: grid;
  position: relative;
  overflow: hidden;
  max-width: 120rem;
`;
const StyledButtons = styled(Buttons)<IButtons>``;
type ColorUpdateHelperArgs = TCopyContentBackgrounds | TCopyContentBackgroundLegacyColors

// superbowl update maps sage => eggplant, lavender => violet
const colorUpdateHelper = (
  color: ColorUpdateHelperArgs,
) => {
  switch (color) {
    case 'eggplant':
    case 'sage':
      return 'eggplant';
    case 'violet':
    case 'lavender':
      return 'violet';
    case 'poppy':
      return 'poppy';
  }
};
// superbowl update requires white text for color contrast accessibility
const styleUpdateHelper = (
  style: TStyledAs,
) => {
  switch (style) {
    case 'quoteLarge':
    case 'quoteLargeWhite':
      return 'quoteLargeWhite';
    case 'quoteSmall':
    case 'quoteSmallWhite':
    default:
      return 'quoteSmallWhite';
  }
};

const SocialProof = ({
  mainBackground = 'light', slides,
}: ISocialProof) => {
  const {
    slides: rows, slideState, swipeable, swipe,
  } = useSlides(slides, ['left', 'right']);

  const firstRender = useFirstRender();

  return (
    <StyledSocialProof $mainBackground={mainBackground} {...swipeable}>
      <SocialProofWrapper>
        <SlideContainer>
          {rows.map((slide: ISocialProofSlide) => (
            <CopyContent key={slide.headlineText} $current={slide.current}
                         $copyContentBackground={colorUpdateHelper(slide.copyContentBackground)} {...slide}>
              {slide.headlineText &&
                <Headline tag="div" styledAs={styleUpdateHelper(slide.headlineStyle)} content={slide.headlineText} />}
              <Spacer>
                {slide?.eyebrowText && <Eyebrow textColor={'white'} content={slide.eyebrowText} />}
              </Spacer>
              {slide?.buttons &&
                <StyledButtons buttons={slide.buttons} variants={['ghost']} buttonColor="light" {...slide.buttons} />}
            </CopyContent>
          ))}
          <ImageWrapper>
            {rows.map((slide: ISocialProofSlide) => (
              <ImageSlider $firstRender={firstRender} $current={slide.current} $previous={slide.previous} $dir={slide.dir} key={slide.headlineText} {...slide}>
                {hasImage(slide.image)
                && (
                <StyledImage
                  altText={slide.image?.altText}
                  imageData={slide.image.gatsbyImage}
                />
                )}
              </ImageSlider>
            ))}
          </ImageWrapper>
          <SlideButtonContainer>
            <ButtonContainer>
              <SlideButton aria-label="Previous Slide" onClick={() => swipe(-1)}>
                <Icon iconName="LongArrowLeft" fillColor={'white'} />
              </SlideButton>
              <SlideButton aria-label="Next Slide" onClick={() => swipe(1)}>
                <Icon iconName="LongArrowRight" fillColor={'white'} />
              </SlideButton>
            </ButtonContainer>
            <ProgressContainer>
              {`${slideState.current + 1}/${slides.length}`}
            </ProgressContainer>
          </SlideButtonContainer>
        </SlideContainer>
      </SocialProofWrapper>
    </StyledSocialProof>
  );
};

export default SocialProof;
